/**
 * RegionAvailabilityBadge.jsx
 *
 * MRT addition per brainstorm §8.2 (no TF v4 equivalent — TF v4 has no
 * per-region availability gating). Renders inline on a PLPProductCard
 * when `useAvailability(itemCode).sellable === false`. Returns null when
 * the product IS sellable in the current region, so consumer code can
 * unconditionally render this component without producing whitespace
 * for in-region products.
 *
 * Consumed in SP3.B.2 by ResultsPage via the PLPProductCard
 * `availabilityBadge` slot prop.
 *
 * Styling:
 *   - Soft amber background using `bg-sandvik-warning/20` (warning token
 *     is `#FFD74B` per tailwind.config.js, scoped under `colors.sandvik`).
 *   - Inline pill, no rounded corners (Sandvik brand).
 *   - Mail icon at 12px to fit the xs text.
 *   - "Contact us" links to `/contact` with `topic=availability`,
 *     `item=<itemCode>`, and `region=<entityCode>` query params. The
 *     region falls back to the empty string when entityCode is falsy.
 *
 * @module components/wizard/RegionAvailabilityBadge
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.B.1)
 */

import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAvailability } from '@hooks/useAvailability';

export default function RegionAvailabilityBadge({ itemCode }) {
  const { sellable, entityCode } = useAvailability(itemCode);

  // When the product IS sellable, render nothing.
  if (sellable !== false) return null;

  const contactPath = `/contact?topic=availability&item=${encodeURIComponent(itemCode)}&region=${encodeURIComponent(entityCode || '')}`;

  return (
    <div
      role="status"
      data-testid="region-availability-badge"
      className="inline-flex items-center gap-1.5 px-2 py-1 bg-sandvik-warning/20 text-carbon-black text-xs font-sandvik-text font-medium"
    >
      <Mail size={12} className="flex-shrink-0" aria-hidden="true" />
      <span>Not available in your region.</span>
      <Link
        to={contactPath}
        className="underline hover:text-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue"
      >
        Contact us
      </Link>
    </div>
  );
}
